import { useState } from "react";
import type { FormEvent } from "react";
import { Search } from "lucide-react";

import { PRO014_ITEM_SORT, PRO014_ITEM_STATUS_FILTER } from "@/@types/campaign";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  ALL_STORES_FILTER,
  NO_STORE_FILTER,
  PRODUCTS_FILTER_KEY,
  PRODUCTS_TEXTS,
} from "../constants";
import type { ProductsFilterKey, ProductsFilters } from "../types";

type FiltersLayout = "legacy" | "standard";

const GRID_CLASS: Record<FiltersLayout, string> = {
  legacy: "grid grid-cols-1 gap-4 md:grid-cols-5",
  standard: "grid grid-cols-1 gap-4 md:grid-cols-4",
};

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: PRO014_ITEM_STATUS_FILTER.ALL, label: PRODUCTS_TEXTS.STATUS_ALL },
  { value: PRO014_ITEM_STATUS_FILTER.ACTIVE, label: PRODUCTS_TEXTS.STATUS_ACTIVE },
  { value: PRO014_ITEM_STATUS_FILTER.INACTIVE, label: PRODUCTS_TEXTS.STATUS_INACTIVE },
];

const SORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: PRO014_ITEM_SORT.CODE_ASC, label: PRODUCTS_TEXTS.SORT_CODE_ASC },
  { value: PRO014_ITEM_SORT.CODE_DESC, label: PRODUCTS_TEXTS.SORT_CODE_DESC },
  { value: PRO014_ITEM_SORT.NAME_ASC, label: PRODUCTS_TEXTS.SORT_NAME_ASC },
  { value: PRO014_ITEM_SORT.NAME_DESC, label: PRODUCTS_TEXTS.SORT_NAME_DESC },
];

interface ProductsFiltersBarProps {
  filters: ProductsFilters;
  showStoreFilter: boolean;
  availableStores: number[];
  onFilterChange: (key: ProductsFilterKey, value: string) => void;
}

export function ProductsFiltersBar({
  filters,
  showStoreFilter,
  availableStores,
  onFilterChange,
}: ProductsFiltersBarProps): JSX.Element {
  const [searchInput, setSearchInput] = useState<string>(filters.search);
  const layout: FiltersLayout = showStoreFilter ? "legacy" : "standard";

  const submitSearch = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onFilterChange(PRODUCTS_FILTER_KEY.SEARCH, searchInput);
  };

  const changeStore = (value: string): void =>
    onFilterChange(PRODUCTS_FILTER_KEY.STORE_CODE, value === ALL_STORES_FILTER ? NO_STORE_FILTER : value);

  return (
    <div className="mb-4 rounded-lg border bg-white p-4 shadow dark:bg-gray-950">
      <div className={GRID_CLASS[layout]}>
        <form onSubmit={submitSearch} className="md:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              placeholder={PRODUCTS_TEXTS.SEARCH_PLACEHOLDER}
              aria-label={PRODUCTS_TEXTS.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className="pl-10"
            />
          </div>
        </form>

        {showStoreFilter && (
          <Select value={filters.storeCode?.toString() ?? ALL_STORES_FILTER} onValueChange={changeStore}>
            <SelectTrigger aria-label={PRODUCTS_TEXTS.STORE_FILTER}>
              <SelectValue placeholder={PRODUCTS_TEXTS.STORE_FILTER} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STORES_FILTER}>{PRODUCTS_TEXTS.ALL_STORES}</SelectItem>
              {availableStores.map((storeCode: number) => (
                <SelectItem key={storeCode} value={storeCode.toString()}>
                  {PRODUCTS_TEXTS.STORE_OPTION(storeCode)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select
          value={filters.status}
          onValueChange={(value: string) => onFilterChange(PRODUCTS_FILTER_KEY.STATUS, value)}
        >
          <SelectTrigger aria-label={PRODUCTS_TEXTS.STATUS_FILTER}>
            <SelectValue placeholder={PRODUCTS_TEXTS.STATUS_FILTER} />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.sort}
          onValueChange={(value: string) => onFilterChange(PRODUCTS_FILTER_KEY.SORT, value)}
        >
          <SelectTrigger aria-label={PRODUCTS_TEXTS.SORT_FILTER}>
            <SelectValue placeholder={PRODUCTS_TEXTS.SORT_FILTER} />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
